'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import type { DirectoryRow } from '@/lib/directory/row'

const shorthand: Record<string, { label: string; key: string }> = {
  'Receiver in Canal': { label: 'RIC', key: 'ric' },
  'Behind the Ear': { label: 'BTE', key: 'bte' },
  'In the Ear': { label: 'ITE', key: 'ite' },
  'In Canal': { label: 'ITC', key: 'itc' },
  Prescription: { label: 'Rx', key: 'rx' },
  OTC: { label: 'OTC', key: 'otc' },
}

const ClassificationLabel = ({ value, facetLabels }: { value: string; facetLabels: Map<string, string> }) => {
  const { t, locale } = useTranslation()
  const label = facetLabels.get(value) ?? value
  const short = shorthand[value]
  if (!short) {
    return label
  }

  return (
    <>
      <span className="hidden md:inline">{label}</span>
      <span className="md:hidden">
        <button
          type="button"
          className="glossary-term"
          data-glossary={short.key}
          // The shared dictionary is English-only and has no Rx entry. Expand
          // those labels locally; existing English terms use its full definition.
          data-glossary-definition={locale !== 'en' || short.key === 'rx' ? label : undefined}
          data-tracking="ignore"
          aria-haspopup="dialog"
          aria-label={`${short.label}: ${label}. ${t('glossary.showDefinition', 'Show definition')}`}
        >
          {short.label}
        </button>
      </span>
    </>
  )
}

interface RowClassificationProps {
  product: DirectoryRow['products'][number] | undefined
  facetLabels: Map<string, string>
}

// Quiet metadata beneath the name, distinct from the model's feature icons.
export const RowClassification = ({ product, facetLabels }: RowClassificationProps) => {
  if (!product) {
    return null
  }
  return (
    <ul className="text-navy/65 flex flex-wrap gap-x-2.5 gap-y-1 text-[12px] leading-snug">
      {product.style ? (
        <li>
          <ClassificationLabel value={product.style} facetLabels={facetLabels} />
        </li>
      ) : null}
      {product.classification ? (
        <li className="inline-flex items-center gap-2.5">
          {product.style ? (
            <span aria-hidden className="text-navy/35">
              ·
            </span>
          ) : null}
          <ClassificationLabel value={product.classification} facetLabels={facetLabels} />
        </li>
      ) : null}
    </ul>
  )
}
